import React, { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useRosettaStore } from '../../../Stores/useRosettaStore';
import { PAYMENT_TERMS_ENUM } from '../../../Constants/Constants';
import { calculatePricingHelper } from '../utils/rosettaPricingHelpers';

const VolumePricingTableCard = () => {
  const {
    tradesData,
    selectedTrades,
    volumeDiscountTiers,
    selectedScopes,
    inflationPercentage,
    perTradeDistribution,
    projectsPerYear,
    sheetsPerProject,
    showTiersTable,
    hiddenTiers,
    paymentTerms
  } = useRosettaStore(
    useShallow(state => ({
      tradesData: state.tradesData,
      selectedTrades: state.selectedTrades,
      volumeDiscountTiers: state.volumeDiscountTiers,
      selectedScopes: state.selectedScopes,
      inflationPercentage: state.inflationPercentage,
      perTradeDistribution: state.perTradeDistribution,
      projectsPerYear: state.projectsPerYear,
      sheetsPerProject: state.sheetsPerProject,
      showTiersTable: state.showTiersTable,
      hiddenTiers: state.hiddenTiers,
      paymentTerms: state.paymentTerms
    }))
  );

  const totalAnnualSheets = useMemo(() => projectsPerYear * sheetsPerProject, [projectsPerYear, sheetsPerProject]);

  const pricing = useMemo(
    () =>
      calculatePricingHelper({
        selectedTrades,
        tradesData,
        selectedScopes,
        inflationPercentage,
        perTradeDistribution,
        totalAnnualSheets,
        volumeDiscountTiers
      }),
    [
      selectedTrades,
      tradesData,
      selectedScopes,
      inflationPercentage,
      perTradeDistribution,
      totalAnnualSheets,
      volumeDiscountTiers
    ]
  );

  const tiers = useMemo(
    () =>
      Object.entries(volumeDiscountTiers)
        .map(([sheetCount, discount]) => ({
          minSheets: parseInt(sheetCount, 10),
          discount: discount,
          tierId: `tier_${sheetCount}`
        }))
        .sort((a, b) => a.minSheets - b.minSheets),
    [volumeDiscountTiers]
  );

  const tableRows = useMemo(() => {
    return tiers
      .map((tier, index) => {
        const sheets = tier.minSheets;
        if (sheets === 0) return null;

        if (hiddenTiers.has(sheets)) return null;

        const volumeDiscount = tier.discount / 100;

        const discountedPrice = pricing.blendedPrice * (1 - volumeDiscount);
        const annualCost = discountedPrice * sheets;
        const quarterlyCost = annualCost / 4;
        const semiAnnualCost = annualCost / 2;

        const nextTier = tiers[index + 1];
        const upperBound = nextTier ? nextTier.minSheets : Infinity;

        const isActive = pricing.totalSheetsFromTrades >= tier.minSheets && pricing.totalSheetsFromTrades < upperBound;

        const showPaymentColumn = paymentTerms !== PAYMENT_TERMS_ENUM.HIDDEN;
        const paymentColumnValue =
          paymentTerms === PAYMENT_TERMS_ENUM.QUARTERLY
            ? quarterlyCost
            : paymentTerms === PAYMENT_TERMS_ENUM.SEMI_ANNUAL
              ? semiAnnualCost
              : null;

        return (
          <tr key={tier.tierId} className={isActive ? 'rosetta-active-tier' : ''}>
            <td>{sheets.toLocaleString()}+</td>
            <td>${discountedPrice.toFixed(1)}</td>
            <td>${annualCost.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
            {showPaymentColumn && <td>${paymentColumnValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>}
          </tr>
        );
      })
      .filter(Boolean);
  }, [tiers, pricing, hiddenTiers, paymentTerms]);

  if (!showTiersTable) {
    return null;
  }

  return (
    <section className='rosetta-pricing-card'>
      <h2 className='rosetta-pricing-title'>Volume Based Pricing Tiers</h2>
      <div className='rosetta-table-wrapper mt-4'>
        <table className='rosetta-pricing-table'>
          <thead>
            <tr>
              <th>Annual Sheets</th>
              <th>Price/Sheet</th>
              <th>Annual Cost</th>
              {paymentTerms !== PAYMENT_TERMS_ENUM.HIDDEN && (
                <th>{paymentTerms === PAYMENT_TERMS_ENUM.QUARTERLY ? 'Quarterly' : 'Semi-Annual'}</th>
              )}
            </tr>
          </thead>
          <tbody>{tableRows}</tbody>
        </table>
      </div>
    </section>
  );
};

export default VolumePricingTableCard;
